import useIdentity from "@/features/ui/hooks/use-identity";
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import AppGrid, {
  AppGridColumn,
  AppGridField,
  AppGridMeta,
} from "@/features/order-sheet-sets/app-grid";
import LineSheetFileTypeGridHeaderCell from "@/features/line-sheet-sets/detail/line-sheet-file-type-grid-header-cell";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import {
  AttachmentRow,
  IsolatedLineSheetRow,
  IsolatedOrderSheetRow,
} from "@/features/line-sheet-sets/detail/isolated-sheet.type";
import {
  isAttachmentRow,
  isLineSheetRow,
  isOrderSheetRow,
  LineSheetableRow,
} from "@/features/line-sheet-sets/detail/utils/line-sheetable-mapper";
import LineSheetFileTypeSelect from "@/features/line-sheet-sets/line-sheet-file-type-select";
import { Box, Flex, FlexProps, Spinner, Text, Tooltip } from "@chakra-ui/react";
import OrderedConfirmedCell from "@/features/line-sheet-sets/detail/ordered-confirmed-cell";
import { toLower } from "@/utils/case";
import { isFallbackLineSheet } from "@/features/line-sheet-sets/utils/line-sheet-set-utils";
import AppLabel from "@/features/ui/app-label";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import {
  GridRow,
  gridRowFactory,
} from "@/features/order-sheet-sets/helpers/app-grid-row-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import LineSheetOrderSheetTable, {
  LineSheetOrderSheetRow,
} from "@/features/line-sheet-sets/detail/line-sheet-order-sheet-table";
import useLineSheetOrderSheetItems, {
  LineSheetOrderSheetItem,
} from "@/features/line-sheet-sets/detail/hooks/use-line-sheet-order-sheet-items";
import { AppLineCardAction } from "@/features/line-sheet-sets/app-flat-card";
import {
  LightOrderSheetSet,
  OrderSheet,
} from "@/features/order-sheets/order-sheet.type";
import useLineSheetableGrid from "@/features/line-sheet-sets/detail/hooks/use-line-sheetable-grid";
import LineSheetReviewStatusGridHeaderCell from "@/features/line-sheet-sets/detail/line-sheet-review-status-grid-header-cell";
import AppReviewStatusSelect from "@/features/ui/app-review-status-select";
import AppReviewStatusLabel from "@/features/ui/app-review-status-label";
import {
  getFileExtension,
  isExcel,
  isPDF,
  isStoredObject,
  StoredObject,
} from "@/features/types";
import useAppGrid from "@/features/invoices/hooks/use-app-grid";
import { NegativeSequencer } from "@/features/ui/utils/negative-sequencer";
import { LineSheetableRowConverter } from "@/features/line-sheet-sets/detail/utils/line-sheetable-row-converter";
import AppOrderSheetStatusLabel from "@/features/ui/app-order-sheet-status-label";
import { useFormikContext } from "formik";
import {
  LineSheetFormField,
  LineSheetSetFormFields,
} from "@/features/line-sheet-sets/detail/line-sheet-set-detail-page";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppInput from "@/features/ui/app-input";
import SVG from "react-inlinesvg";
import BrandSelectModal from "@/shared/brand-select-modal";
import { highlightPartialText } from "@/utils/highlightSearch";
import { BuyerLineSheetSetRow } from "@/features/order-sheet-sets/line-sheet-set-row.type";
import usePersistedLineSheetSetDetailPageQuery from "@/features/line-sheet-sets/detail/use-persisted-line-sheet-sheet-set-detail-page-query";
import useGetCompanies from "@/features/order-sheet-sets/hooks/useGetCompanies";
import OrderStatusBadge from "@/features/order-status/OrderStatusBadge";

/**
 * 라인시트세트 수정 시 라인시트 이름을 인라인 편집하는 셀.
 * 타이핑 중엔 로컬 상태만 갱신(그리드 리렌더 없음 → 포커스 유지)하고,
 * 블러 시점에만 상위 rows(→formik)로 반영한다. 빈 이름은 원래 값으로 되돌린다.
 */
function LineSheetTitleEditCell({
  initialName,
  onCommit,
}: {
  initialName: string;
  onCommit: (name: string) => void;
}) {
  const [value, setValue] = useState(initialName);

  useEffect(() => {
    setValue(initialName);
  }, [initialName]);

  return (
    // 셀 클릭이 행 onClick(=파일 선택 열기)으로 전파되지 않도록 막는다.
    <Box
      width={"100%"}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <AppInput
        value={value}
        onChange={(v) => setValue(v ?? "")}
        onBlur={() => {
          const trimmed = value.trim();
          if (trimmed && trimmed !== initialName) {
            onCommit(trimmed);
          } else if (!trimmed) {
            setValue(initialName);
          }
        }}
      />
    </Box>
  );
}

// 라인시트 행에서 오더 목록을 접고 펼치는 자리. 누르면 행 클릭(라인시트 상세 열기)으로 번지지 않는다.
function OrderToggleArea({
  isCollapsed,
  onToggle,
  children,
  ...flexProps
}: {
  isCollapsed: boolean;
  onToggle: () => void;
  children: ReactNode;
} & FlexProps) {
  return (
    <Flex
      role={"button"}
      aria-expanded={!isCollapsed}
      cursor={"pointer"}
      alignItems={"center"}
      borderRadius={"4px"}
      _hover={{ background: "#F2F4F7" }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      {...flexProps}
    >
      {children}
    </Flex>
  );
}

function sumBy<T>(items: T[], pick: (item: T) => number | null | undefined) {
  return items.reduce((acc, item) => acc + (pick(item) || 0), 0);
}

interface LineSheetGridMetaParameter {
  isEditing?: boolean;
  rows?: LineSheetableRow[];
  setRows: (
    valueOrUpdater:
      | LineSheetableRow[]
      | ((
          prev: LineSheetableRow[] | undefined
        ) => LineSheetableRow[] | undefined)
  ) => void;
  isRefreshing: boolean;
  orderSheetSet?: LightOrderSheetSet;
  fileInputRef: React.RefObject<HTMLInputElement>;
  sequencer: NegativeSequencer;
  lineSheetableGrid: ReturnType<typeof useLineSheetableGrid>;
}

export default function LineSheetableGrid({
  isEditing = false,
  rows,
  setRows,
  isRefreshing,
  orderSheetSet,
  fileInputRef,
  sequencer,
  lineSheetableGrid,
}: LineSheetGridMetaParameter) {
  const { values } = useFormikContext<LineSheetSetFormFields>();
  const fileTargetRef = useRef<IsolatedLineSheetRow | AttachmentRow>();
  const {
    t,
    tTitle,
    tUpper,
    tCurrencyString,
    tConditionAppliedCurrencyString,
    tLocalDateString,
  } = useI18nHelper();
  const identity = useIdentity();
  const [isOpen, setIsOpen] = React.useState(false);
  const [brandsForModal, setBrandsForModal] = React.useState<any[]>([]);
  const [lineSheetIdForModal, setLineSheetIdForModal] =
    React.useState<number>(0);

  const rowConverter = useMemo(() => {
    return new LineSheetableRowConverter(sequencer);
  }, [sequencer]);
  const { value: query } = usePersistedLineSheetSetDetailPageQuery();

  const openFileInput = useCallback(
    (row: GridRow) => {
      fileTargetRef.current = row as IsolatedLineSheetRow | AttachmentRow;
      fileInputRef.current?.click();
    },
    [fileInputRef]
  );

  const saveSelectedBrand = useCallback(
    (selectedBrands: any[], lineSheetId: number) => {
      setRows((prev) => {
        const brands = selectedBrands.map((brand) => {
          return {
            id: brand.id,
            name: brand.name,
          };
        });
        return prev?.map((row) => {
          if (row.id === lineSheetId) {
            return {
              ...row,
              brands: brands || [],
            };
          }
          return row;
        });
      });
      setBrandsForModal(selectedBrands);
    },
    [setRows]
  );

  const handleFileChange = useCallback(
    (event: Event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0 && fileTargetRef.current) {
        const file = target.files[0];

        setRows((prev) => {
          if (prev) {
            return prev.map((row) => {
              if (row.id === fileTargetRef.current?.id) {
                return rowConverter.convert(fileTargetRef.current, {
                  file,
                });
              }
              return row;
            });
          }
        });
      }
    },
    [rowConverter, setRows]
  );

  useEffect(() => {
    const fileInputElement = fileInputRef.current;

    if (fileInputElement) {
      fileInputRef.current.addEventListener("change", handleFileChange);
    }

    return () => {
      if (fileInputElement) {
        fileInputElement.removeEventListener("change", handleFileChange);
      }
    };
  }, [fileInputRef, handleFileChange]);

  const withEditOnlyGridAction = useCallback(
    (actions: ((row: GridRow) => AppLineCardAction | undefined)[]) => {
      if (isEditing) {
        return actions;
      }
      return [];
    },
    [isEditing]
  );

  const withReadOnlyGridActions = useCallback(
    (actions: ((row: GridRow) => AppLineCardAction | undefined)[]) => {
      if (!isEditing) {
        return actions;
      }
      return [];
    },
    [isEditing]
  );

  const allBrandList = useGetCompanies(["BRAND"]);

  const selectedBrands = useMemo(() => {
    return (
      query["madeByCompany.id__in"]
        ?.map(
          (company) =>
            allBrandList?.find((brand) => brand.id === +company)?.name
        )
        .filter((brand): brand is string => !!brand) ?? []
    );
  }, [allBrandList, query]);

  const indicator = useMemo((): AppGridMeta["indicator"] => {
    // eslint-disable-next-line react/display-name
    return (row) => {
      const lineSheetRow = row as IsolatedLineSheetRow;
      if (!isEditing) {
        if (lineSheetRow.status) {
          if (isFallbackLineSheet({ status: lineSheetRow.status })) {
            return (
              <GridRowIndicator type={"DOWNLOAD"} isHovered={row.isHovered} />
            );
          } else {
            return <GridRowIndicator type={"VIEW"} isHovered={row.isHovered} />;
          }
        }
      }
      return <GridRowIndicator type={"EMPTY"} isHovered={row.isHovered} />;
    };
  }, [isEditing]);

  type LineSheetGridKey =
    | "line_sheet_set.type"
    | "title"
    | "brand"
    | "status"
    | "review_status";

  const lineSheetGridPairs = useMemo((): {
    [key in LineSheetGridKey]: { column: AppGridColumn; field: AppGridField };
  } => {
    return {
      "line_sheet_set.type": {
        column: {
          name: tTitle("line_sheet_set.type"),
          width: isEditing ? "140px" : "80px",
          value: (
            <LineSheetFileTypeGridHeaderCell
              rows={rows}
              isEditing={isEditing}
              onSelect={(type) => {
                setRows((prev) => {
                  return prev?.map((row) => {
                    if (row.isChecked) {
                      if (isLineSheetRow(row)) {
                        return rowConverter.convert(row, { type });
                      } else if (isAttachmentRow(row)) {
                        const fileLike = row.file || {
                          id: row.id,
                          name: row.name,
                        };
                        const fileIsExcel = isExcel(fileLike);
                        const fileIsPdf = isPDF(fileLike);
                        if (type === "ORDER_FORM" && !fileIsExcel) {
                          return row;
                        }
                        if (
                          type === "LINE_SHEET" &&
                          !fileIsExcel &&
                          !fileIsPdf
                        ) {
                          return row;
                        }
                        return rowConverter.convert(row, {
                          type,
                        });
                      }
                    }
                    return row;
                  });
                });
              }}
            />
          ),
        },
        field: {
          name: "line_sheet_set.type",
          value: (row) => {
            const lineSheetRow = row as IsolatedLineSheetRow;
            const isApiOrder = values.type === "API_ORDER";
            if (isEditing) {
              return (
                <LineSheetFileTypeSelect
                  value={isApiOrder ? "LINE_SHEET" : lineSheetRow.type}
                  isDisabled={isApiOrder}
                  isExcel={true}
                  onSelect={(lineSheetType) => {
                    setRows((prev) => {
                      return prev?.map((row) => {
                        if (row.id === lineSheetRow.id) {
                          return rowConverter.convert(row, {
                            type: lineSheetType,
                          });
                        }
                        return row;
                      });
                    });
                  }}
                />
              );
            } else {
              switch (identity?.company?.type) {
                case "AGENCY":
                  return <Text>{tUpper(toLower(lineSheetRow.type))}</Text>;
                default:
                  let key: string = "line_sheet";
                  if (isFallbackLineSheet({ status: lineSheetRow.status! })) {
                    key = "order_form";
                  }
                  return <Text>{tUpper(toLower(key))}</Text>;
              }
            }
          },
        },
      },
      title: {
        column: {
          name: tTitle("title"),
          width: "minmax(100px, 1fr)",
        },
        field: {
          name: "title",
          value: (row) => {
            const { id, name, searchedSkus } = row as IsolatedOrderSheetRow;

            // 수정 모드에서는 라인시트(라인시트/오더폼) 이름을 인라인 편집할 수 있게 한다.
            if (isEditing && isLineSheetRow(row)) {
              return (
                <LineSheetTitleEditCell
                  initialName={name}
                  onCommit={(newName) => {
                    setRows((prev) =>
                      prev?.map((r) =>
                        isLineSheetRow(r) && r.id === row.id
                          ? { ...r, name: newName }
                          : r
                      )
                    );
                  }}
                />
              );
            }

            const title = highlightPartialText(name, [query.query]);
            const refinedSearchedSkus = highlightPartialText(
              searchedSkus?.at(0),
              [query.query]
            );
            return (
              <Flex flexDirection={"column"}>
                <Text textStyle={"body-sm-bold"}>
                  #{id} {title}
                </Text>
                {searchedSkus && searchedSkus.length > 0 && (
                  <Text textStyle={"caption-regular"}>
                    ...{refinedSearchedSkus}
                  </Text>
                )}
              </Flex>
            );
            // const { id, name } = row as IsolatedLineSheetRow;
            // if (identity?.company?.types === "AGENCY") {
            //   return `#${id} ${name}`;
            // } else {
            //   return name;
            // }
          },
        },
      },
      brand: {
        column: {
          name: tTitle("brand"),
          width: "160px",
        },
        field: {
          name: "brand",
          value: (row) => {
            const lineSheetRow = row as IsolatedLineSheetRow;
            const isAllProductHaveBrand = (
              lineSheetRow.source as LineSheetFormField
            ).isAllProductsHaveBrand;
            const brands = lineSheetRow.brands;
            const term = query.query;
            const refinedBrands = brands.map((brand) => brand.name);
            const keywords = [term, ...selectedBrands]
              .filter((kw): kw is string => !!kw)
              .join("|");
            const regex = new RegExp(keywords, "i");

            const matching = refinedBrands.filter((brand: string) =>
              regex.test(brand)
            );
            const others = refinedBrands.filter(
              (brand: string) => !regex.test(brand)
            );
            // 3) 검색어에 매칭되는 브랜드를 분리
            // const matching = refinedBrands.filter((b: string) =>
            //   b.toLowerCase().includes(term?.toLowerCase() ?? "")
            // );
            // const others = refinedBrands.filter(
            //   (b: string) =>
            //     !b.toLowerCase().includes(term?.toLowerCase() ?? "")
            // );

            // 4) 매칭된 순서 유지(혹은 하나만) + 나머지 알파벳 순 정렬
            const orderedBrands = [...matching, ...others];
            const brandList = orderedBrands.join(", ");

            return !isEditing ||
              isAllProductHaveBrand ||
              (lineSheetRow.id < 0 && lineSheetRow.type != "ORDER_FORM") ? (
              <Flex flexDirection={"row"}>
                {brands.length == 0 &&
                  lineSheetRow.status &&
                  lineSheetRow.status != "PARSED" &&
                  identity?.company?.type === "AGENCY" && (
                    <Box marginRight={"8px"} alignSelf={"center"}>
                      <SVG
                        src={"/icons-v1_1/warning-circle.svg"}
                        color={"#D4D4D1"}
                      />
                    </Box>
                  )}
                <Tooltip
                  label={highlightPartialText(
                    brandList,
                    [query.query, ...selectedBrands],
                    true
                  )}
                  hasArrow={true}
                  placement={"top"}
                  openDelay={500}
                >
                  <Text noOfLines={2}>
                    {highlightPartialText(
                      brandList.length > 0 ? brandList : " - ",
                      [query.query, ...selectedBrands]
                    )}
                  </Text>
                </Tooltip>
              </Flex>
            ) : (
              <Flex flexDirection={"row"}>
                {brands.length == 0 &&
                  lineSheetRow.status &&
                  lineSheetRow.status != "PARSED" && (
                    <Box marginRight={"8px"} alignSelf={"center"}>
                      <SVG
                        src={"/icons-v1_1/warning-circle.svg"}
                        color={"#D4D4D1"}
                      />
                    </Box>
                  )}
                <AppButton
                  size={"medium"}
                  width={"100%"}
                  height={"28px"}
                  onClick={(e) => {
                    e.stopPropagation();
                    setBrandsForModal(brands);
                    setLineSheetIdForModal(lineSheetRow.id);
                    setIsOpen(true);
                  }}
                >
                  <Flex gap={"4px"} alignItems={"center"}>
                    <Text textStyle="body-sm-bold" color={"#444444"}>
                      {t(
                        brands.length > 1
                          ? "issued_by_brands_others"
                          : "issued_by_brands_one",
                        {
                          first:
                            brands.length == 0
                              ? tTitle("select")
                              : brands[0].name,
                          count: brands.length - 1,
                        }
                      )}
                    </Text>
                  </Flex>
                </AppButton>
              </Flex>
            );
          },
        },
      },
      status: {
        column: {
          name: tTitle("status"),
          width: "132px",
        },
        field: {
          name: "status",
          value: (row) => {
            const lineSheetRow = row as IsolatedLineSheetRow;

            if (identity?.company?.type === "AGENCY") {
              if (lineSheetRow.status) {
                if (lineSheetRow.status === "PARSING_PENDING" && isRefreshing) {
                  return <Spinner />;
                }
                return (
                  <AppLabel
                    nameKey={`line_sheet.status.${toLower(
                      lineSheetRow.status
                    )}`}
                    level={(() => {
                      const status = lineSheetRow.status;
                      if (status === "PARSING_FAILED") {
                        return "ERROR";
                      } else if (status === "PARSED") {
                        return "SUCCESS";
                      }
                      return "LIGHT";
                    })()}
                  />
                );
              }
            }

            return "";
          },
        },
      },
      review_status: {
        column: {
          name: tTitle("review_status"),
          width: "128px",
          value: (
            <LineSheetReviewStatusGridHeaderCell
              rows={rows}
              isEditing={isEditing}
              onChange={(reviewStatus) => {
                setRows((prev) => {
                  return prev?.map((row) => {
                    if (row.isChecked) {
                      if (isLineSheetRow(row)) {
                        return {
                          ...row,
                          reviewStatus,
                        };
                      }
                    }

                    return row;
                  });
                });
              }}
            />
          ),
        },
        field: {
          name: "review_status",
          value: (row) => {
            const lineSheetRow = row as IsolatedLineSheetRow;
            if (isEditing && lineSheetRow.type === "LINE_SHEET") {
              return (
                <AppReviewStatusSelect
                  value={lineSheetRow.reviewStatus}
                  onSelect={(reviewStatus) => {
                    setRows((prev) => {
                      return prev?.map((row) => {
                        if (row.id === lineSheetRow.id) {
                          return {
                            ...row,
                            reviewStatus,
                          };
                        }
                        return row;
                      });
                    });
                  }}
                />
              );
            } else if (lineSheetRow.reviewStatus) {
              return <AppReviewStatusLabel value={lineSheetRow.reviewStatus} />;
            }
            return "";
          },
        },
      },
    };
  }, [
    tTitle,
    isEditing,
    rows,
    setRows,
    rowConverter,
    values.type,
    identity?.company?.type,
    tUpper,
    query.query,
    selectedBrands,
    t,
    isRefreshing,
  ]);

  type AttachmentGridKey = "type" | "name";

  const attachmentGridPairs = useMemo((): {
    [key in AttachmentGridKey]: {
      column: AppGridColumn;
      field: AppGridField;
    };
  } => {
    return {
      type: {
        column: {
          name: tTitle("type"),
          width: isEditing ? "140px" : "120px",
        },
        field: {
          name: "type",
          value: (row) => {
            const attachmentRow = row as AttachmentRow;
            if (isEditing) {
              return (
                <LineSheetFileTypeSelect
                  value={
                    attachmentRow.isOrderRule ? "ORDER_RULE" : "ATTACHMENT"
                  }
                  isExcel={isExcel(
                    attachmentRow.file || {
                      id: attachmentRow.id,
                      name: attachmentRow.name,
                    }
                  )}
                  isPdf={isPDF(
                    attachmentRow.file || {
                      id: attachmentRow.id,
                      name: attachmentRow.name,
                    }
                  )}
                  onSelect={(lineSheetType) => {
                    setRows((prev) => {
                      return prev?.map((row) => {
                        if (row.id === attachmentRow.id) {
                          return rowConverter.convert(row, {
                            type: lineSheetType,
                          });
                        }
                        return row;
                      });
                    });
                  }}
                />
              );
            } else {
              return (
                <Text>{`${
                  tUpper(
                    attachmentRow.isOrderRule ? "order_rule" : "attachment"
                  ) +
                  "/" +
                  getFileExtension(attachmentRow.name)
                }`}</Text>
              );
            }
          },
        },
      },
      name: {
        column: {
          name: tTitle("title"),
          width: "minmax(100px, 1fr)",
        },
        field: {
          name: "name",
          value: (row) => {
            const attachmentRow = row as AttachmentRow;
            const { id, name } = attachmentRow;
            if (identity?.company?.type === "AGENCY") {
              return `#${id} ${name}`;
            } else {
              return name;
            }
          },
        },
      },
    };
  }, [
    identity?.company?.type,
    isEditing,
    rowConverter,
    setRows,
    tTitle,
    tUpper,
  ]);

  const onCheck = useCallback(
    (row: GridRow, checked: boolean) => {
      setRows((prev) => {
        return prev?.map((r) => {
          if (r.id === row.id) {
            return { ...r, isChecked: checked };
          }
          return r;
        });
      });
    },
    [setRows]
  );

  const onCheckAll = useCallback(
    (checked: boolean) => {
      setRows((prev) => {
        return prev?.map((r) => {
          return { ...r, isChecked: checked };
        });
      });
    },
    [setRows]
  );

  const attachmentGridMeta = useMemo((): AppGridMeta => {
    const onClick: AppGridMeta["onClick"] = (row) => {
      if (isEditing) {
        openFileInput(row);
      } else {
        lineSheetableGrid.actions.downloadAttachment(row)?.onClick();
      }
    };
    switch (identity?.company?.type) {
      default:
        return {
          gap: "8px",
          rowType: "Attachment",
          isCheckable: isEditing,
          columns: [
            attachmentGridPairs.type.column,
            attachmentGridPairs.name.column,
          ],
          fields: [
            attachmentGridPairs.type.field,
            attachmentGridPairs.name.field,
          ],
          indicator,
          actions: [
            ...withEditOnlyGridAction([
              lineSheetableGrid.actions.deleteLineSheetable,
            ]),
          ],
          onClick,
          onCheck,
          onCheckAll,
        };
    }
  }, [
    attachmentGridPairs.name.column,
    attachmentGridPairs.name.field,
    attachmentGridPairs.type.column,
    attachmentGridPairs.type.field,
    identity?.company?.type,
    indicator,
    isEditing,
    lineSheetableGrid.actions,
    onCheck,
    onCheckAll,
    openFileInput,
    withEditOnlyGridAction,
  ]);

  const lineSheetGridMeta = useMemo((): AppGridMeta => {
    const onClick: AppGridMeta["onClick"] = (row) => {
      if (isEditing) {
        openFileInput(row);
      } else {
        const lineSheetRow = row as IsolatedLineSheetRow;

        // if (identity?.company?.types === "BOUTIQUE") {
        //   lineSheetableGrid.actions.downloadLineSheet(row)?.onClick();
        //   return;
        // }

        if (
          isFallbackLineSheet({
            status: lineSheetRow.status!,
          })
        ) {
          lineSheetableGrid.actions.downloadLineSheet(row)?.onClick();
        } else if (lineSheetRow.status === "PARSED") {
          const action =
            lineSheetableGrid.actions.writeDraftOrViewLineSheet(row);
          if (action && !action.isLoading) {
            action.onClick();
          }
        }
      }
    };

    switch (identity?.company?.type) {
      case "BUYER":
        return {
          gap: "8px",
          rowType: "LineSheet",
          isCheckable: true,
          columns: [
            lineSheetGridPairs["line_sheet_set.type"].column,
            lineSheetGridPairs.title.column,
            lineSheetGridPairs.brand.column,
            {
              name: tTitle("ordered_amount"),
              width: "128px",
            },
            {
              name: tTitle("status"),
              width: "120px",
            },
          ],
          fields: [
            lineSheetGridPairs["line_sheet_set.type"].field,
            lineSheetGridPairs.title.field,
            lineSheetGridPairs.brand.field,
            {
              name: "ordered_amount",
              value: (_row) => {
                return "";
              },
            },
            {
              name: "status",
              value: (_row) => {
                return "";
              },
            },
          ],
          indicator,
          actions: [
            ...withEditOnlyGridAction([
              lineSheetableGrid.actions.deleteLineSheetable,
            ]),
            ...withReadOnlyGridActions([
              lineSheetableGrid.actions.writeDraftOrViewLineSheet,
              lineSheetableGrid.actions.downloadLineSheet,
              lineSheetableGrid.actions.downloadLineSheetUnoForm,
              lineSheetableGrid.actions.reparseLineSheet,
              lineSheetableGrid.actions.forceLineSheetParsingFailed,
              lineSheetableGrid.actions.pendReview,
              lineSheetableGrid.actions.approveReview,
              lineSheetableGrid.actions.rejectReview,
            ]),
          ],
          onClick,
        };
      case "AGENCY":
        let columns: AppGridColumn[];
        let fields: AppGridField[];
        if (orderSheetSet) {
          columns = [
            lineSheetGridPairs["line_sheet_set.type"].column,
            lineSheetGridPairs.title.column,
            lineSheetGridPairs.brand.column,
            {
              name: tTitle("ordered_amount"),
              width: "120px",
            },
            lineSheetGridPairs.status.column,
          ];
          fields = [
            lineSheetGridPairs["line_sheet_set.type"].field,
            lineSheetGridPairs.title.field,
            lineSheetGridPairs.brand.field,
            {
              name: "ordered_amount",
              value: (_row) => {
                return (
                  <Flex
                    flexDirection={"column"}
                    alignItems={"flex-end"}
                    marginRight={"8px"}
                  >
                    <Box fontWeight={"700"}></Box>
                    <Box>-</Box>
                  </Flex>
                );
              },
            },
            lineSheetGridPairs.status.field,
          ];
        } else {
          columns = [
            lineSheetGridPairs["line_sheet_set.type"].column,
            lineSheetGridPairs.title.column,
            lineSheetGridPairs.brand.column,
            lineSheetGridPairs.status.column,
          ];
          fields = [
            lineSheetGridPairs["line_sheet_set.type"].field,
            lineSheetGridPairs.title.field,
            lineSheetGridPairs.brand.field,
            lineSheetGridPairs.status.field,
          ];
        }
        return {
          gap: "8px",
          rowType: "LineSheet",
          isCheckable: isEditing,
          columns: columns,
          fields: fields,
          indicator,
          actions: [
            ...withEditOnlyGridAction([
              lineSheetableGrid.actions.deleteLineSheetable,
            ]),
            ...withReadOnlyGridActions([
              lineSheetableGrid.actions.writeDraftOrViewLineSheet,
              lineSheetableGrid.actions.downloadLineSheet,
              lineSheetableGrid.actions.downloadLineSheetUnoForm,
              lineSheetableGrid.actions.reparseLineSheet,
              lineSheetableGrid.actions.forceLineSheetParsingFailed,
              lineSheetableGrid.actions.pendReview,
              lineSheetableGrid.actions.approveReview,
              lineSheetableGrid.actions.rejectReview,
            ]),
          ],
          onClick,
          onCheck,
          onCheckAll,
        };
      case "BOUTIQUE":
        return {
          gap: "8px",
          isCheckable: isEditing,
          rowType: "LineSheet",
          columns: [
            lineSheetGridPairs["line_sheet_set.type"].column,
            lineSheetGridPairs.title.column,
            lineSheetGridPairs.brand.column,
          ],
          fields: [
            lineSheetGridPairs["line_sheet_set.type"].field,
            lineSheetGridPairs.title.field,
            lineSheetGridPairs.brand.field,
          ],
          indicator,
          actions: [
            ...withEditOnlyGridAction([
              lineSheetableGrid.actions.deleteLineSheetable,
            ]),
            // 라인시트별 모든 오더 수량 합계 엑셀(발행사 양식·우노폼 × 주문·확정 수량)
            ...withReadOnlyGridActions([
              lineSheetableGrid.actions.downloadOrderTotalOriginOrdered,
              lineSheetableGrid.actions.downloadOrderTotalOriginConfirmed,
              lineSheetableGrid.actions.downloadOrderTotalUnoFormOrdered,
              lineSheetableGrid.actions.downloadOrderTotalUnoFormConfirmed,
            ]),
          ],
          onClick,
          onCheck,
          onCheckAll,
        };
      default: {
        return {
          gap: "8px",
          rowType: "LineSheet",
          columns: [],
          fields: [],
          actions: [],
          indicator,
        };
      }
    }
  }, [
    identity?.company?.type,
    isEditing,
    openFileInput,
    lineSheetableGrid.actions,
    lineSheetGridPairs,
    tTitle,
    indicator,
    withEditOnlyGridAction,
    withReadOnlyGridActions,
    orderSheetSet,
    onCheck,
    onCheckAll,
  ]);

  type OrderSheetGridKey =
    | "type"
    | "name"
    | "brand"
    | "submitTo"
    | "orderedAmount"
    | "confirmedAmount"
    | "status";

  const orderSheetGridPairs = useMemo((): {
    [key in OrderSheetGridKey]: {
      column: AppGridColumn;
      field: AppGridField;
    };
  } => {
    return {
      type: {
        column: {
          name: tTitle("type"),
          width: "80px",
        },
        field: {
          name: "type",
          value: (row) => {
            const orderSheetRow = row as IsolatedOrderSheetRow;
            let key: string = "line_sheet";
            if (isFallbackLineSheet(orderSheetRow.lineSheet)) {
              key = "order_form";
            }
            return <Text>{tUpper(toLower(key))}</Text>;
          },
        },
      },
      name: {
        column: {
          name: tTitle("title"),
          width: "minmax(100px, 1fr)",
        },
        field: {
          name: "name",
          value: (row) => {
            const { id, name, lineSheet } = row as IsolatedOrderSheetRow;
            const searchedSkus = lineSheet?.searchedSkus;

            const title = highlightPartialText(`#${id} ${name}`, [query.query]);
            const refinedSearchedSkus = highlightPartialText(
              searchedSkus?.at(0),
              [query.query]
            );
            return (
              <Flex flexDirection={"column"}>
                <Text textStyle={"body-sm-bold"}>{title}</Text>
                {searchedSkus && searchedSkus.length > 0 && (
                  <Text textStyle={"caption-regular"}>
                    ...{refinedSearchedSkus}
                  </Text>
                )}
              </Flex>
            );
          },
        },
      },
      brand: {
        column: {
          name: tTitle("brand"),
          width: "160px",
        },
        field: {
          name: "brand",
          value: (row) => {
            const orderSheetRow = row as IsolatedOrderSheetRow;
            const brands = orderSheetRow.lineSheet.brands.map(
              (brand) => brand.name
            );
            const term = query.query;

            const keywords = [term, ...selectedBrands]
              .filter((kw): kw is string => !!kw)
              .join("|");
            const regex = new RegExp(keywords, "i");

            const matching = brands.filter((brand: string) =>
              regex.test(brand)
            );
            const others = brands.filter((brand: string) => !regex.test(brand));

            // 3) 검색어에 매칭되는 브랜드를 분리
            // const matching = brands.filter((b: string) =>
            //   b.toLowerCase().includes(term?.toLowerCase() ?? "")
            // );
            // const others = brands.filter(
            //   (b: string) =>
            //     !b.toLowerCase().includes(term?.toLowerCase() ?? "")
            // );

            // 4) 매칭된 순서 유지(혹은 하나만) + 나머지 알파벳 순 정렬
            const orderedBrands = [...matching, ...others];

            // 5) 문자열로 합치기
            const brandList = orderedBrands.join(", ");
            return (
              <Flex flexDirection={"row"}>
                <Tooltip
                  label={highlightPartialText(
                    brandList,
                    [query.query, ...selectedBrands],
                    true
                  )}
                  hasArrow={true}
                  placement={"top"}
                  openDelay={500}
                >
                  <Text noOfLines={2}>
                    {highlightPartialText(
                      brandList.length > 0 ? brandList : " - ",
                      [query.query, ...selectedBrands]
                    )}
                  </Text>
                </Tooltip>
              </Flex>
            );
          },
        },
      },
      submitTo: {
        column: {
          name: tTitle("submit_to"),
          width: "80px",
        },
        field: {
          name: "submit_to",
          value: (row) => {
            const orderSheetRow = row as IsolatedOrderSheetRow;
            if (orderSheetRow.toBeSubmitted) {
              return orderSheetRow.toBeSubmitted.name;
            }
            return "";
          },
        },
      },
      orderedAmount: {
        column: {
          name: tTitle("ordered_amount.abbr"),
          width: "120px",
          textAlign: "end",
          marginRight: "8px",
        },
        field: {
          name: "ordered_amount",
          value: (row) => {
            const orderSheetRow = row as IsolatedOrderSheetRow;
            if (
              orderSheetRow.status !== "INITIAL_DRAFT" &&
              orderSheetRow.orderedAmountWithoutCondition !== 0 &&
              orderSheetRow.orderedAmount !== 0
            ) {
              return (
                <Flex
                  flexDirection={"column"}
                  alignItems={"flex-end"}
                  marginRight={"8px"}
                >
                  <Box fontWeight={"700"}>
                    {tCurrencyString(
                      orderSheetRow.orderedAmountWithoutCondition,
                      { type: "SYMBOL", code: values.currency }
                    )}
                  </Box>
                  <Box>
                    {tConditionAppliedCurrencyString(
                      orderSheetRow.orderedConditionType,
                      orderSheetRow.orderedAmount,
                      { type: "SYMBOL", code: values.currency }
                    )}
                  </Box>
                </Flex>
              );
            }
            return "";
          },
        },
      },
      confirmedAmount: {
        column: {
          name: tTitle("confirmed_amount.abbr"),
          width: "120px",
          textAlign: "end",
          marginRight: "8px",
        },
        field: {
          name: "confirmed_amount",
          value: (row) => {
            const orderSheetRow = row as IsolatedOrderSheetRow;
            if (orderSheetRow.confirmedAmount > 0) {
              return (
                <Flex
                  flexDirection={"column"}
                  alignItems={"flex-end"}
                  marginRight={"8px"}
                >
                  <Box fontWeight={"700"}>
                    {tCurrencyString(
                      orderSheetRow.confirmedAmountWithoutCondition,
                      { type: "SYMBOL", code: values.currency }
                    )}
                  </Box>
                  <Box>
                    {tConditionAppliedCurrencyString(
                      orderSheetRow.confirmedConditionType,
                      orderSheetRow.confirmedAmount,
                      { type: "SYMBOL", code: values.currency }
                    )}
                  </Box>
                </Flex>
              );
            }
            return "";
          },
        },
      },
      status: {
        column: {
          name: tTitle("status"),
          width: "128px",
        },
        field: {
          name: "status",
          value: (row) => {
            const orderSheetRow = row as IsolatedOrderSheetRow;
            // return <AppOrderSheetStatusLabel value={orderSheetRow.status} />;
            return <OrderStatusBadge status={orderSheetRow.status} />;
          },
        },
      },
    };
  }, [
    query.query,
    selectedBrands,
    tConditionAppliedCurrencyString,
    tCurrencyString,
    tTitle,
    tUpper,
    values.currency,
  ]);

  const orderSheetGridMeta = useMemo((): AppGridMeta => {
    const onClick: AppGridMeta["onClick"] = (row) => {
      const orderSheetRow = row as IsolatedOrderSheetRow;
      if (!isEditing) {
        if (isFallbackLineSheet(orderSheetRow.lineSheet)) {
          lineSheetableGrid.actions.downloadLineSheet(row)?.onClick();
        } else {
          lineSheetableGrid.actions.editOrViewOrderSheet(row)?.onClick();
        }
      }
    };

    const onCheck: AppGridMeta["onCheck"] = (row, checked) => {
      const orderSheetRow = row as IsolatedOrderSheetRow;
      setRows((prev) => {
        if (prev) {
          let statues = new Set<OrderSheet["status"]>();
          const next = prev.map((row) => {
            if (isOrderSheetRow(row)) {
              if (row.id === orderSheetRow.id) {
                statues.add(row.status);
                return { ...row, isChecked: checked };
              }
              return row;
            }
            return row;
          });

          if (statues.size === 0) {
            return next.map((row) => {
              if (isOrderSheetRow(row)) {
                return { ...row, isCheckable: true };
              }
              return row;
            });
          } else if (statues.size === 1) {
            return next.map((row) => {
              if (isOrderSheetRow(row)) {
                if (row.status === statues.values().next().value) {
                  return { ...row, isCheckable: true };
                }
              }
              return row;
            });
          } else {
            throw {
              message:
                "Cannot check multiple order sheets with different status",
            };
          }
        }

        return prev;
      });
    };

    const onCheckAll: AppGridMeta["onCheckAll"] = (checked) => {
      setRows((prev) => {
        if (prev) {
          return prev.map((row) => {
            if (isOrderSheetRow(row) && row.isCheckable) {
              return { ...row, isChecked: checked };
            }
            return row;
          });
        }
        return prev;
      });
    };

    switch (identity?.company?.type) {
      case "AGENCY":
        return {
          gap: "8px",
          rowType: "OrderSheet",
          isCheckable: false,
          showCheckAll: false,
          columns: [
            orderSheetGridPairs.type.column,
            orderSheetGridPairs.name.column,
            orderSheetGridPairs.brand.column,
            orderSheetGridPairs.orderedAmount.column,
            ...(orderSheetSet && orderSheetSet.confirmedAmount > 0
              ? [orderSheetGridPairs.confirmedAmount.column]
              : []),
            orderSheetGridPairs.status.column,
          ],
          fields: [
            orderSheetGridPairs.type.field,
            orderSheetGridPairs.name.field,
            orderSheetGridPairs.brand.field,
            orderSheetGridPairs.orderedAmount.field,
            ...(orderSheetSet && orderSheetSet.confirmedAmount > 0
              ? [orderSheetGridPairs.confirmedAmount.field]
              : []),
            orderSheetGridPairs.status.field,
          ],
          indicator,
          extraContents:
            lineSheetableGrid.extraContents.orderSheetExtraContents,
          actions: [
            ...withEditOnlyGridAction([
              lineSheetableGrid.actions.deleteLineSheetable,
            ]),
            ...withReadOnlyGridActions([
              lineSheetableGrid.actions.writeDraftByOrderSheet,
              lineSheetableGrid.actions.editOrViewOrderSheet,
              lineSheetableGrid.actions.uploadFallbackOrderSheet,
              lineSheetableGrid.actions.deleteOrderSheet,
              lineSheetableGrid.actions.downloadLineSheet,
              lineSheetableGrid.actions.downloadLineSheetUnoForm,
              lineSheetableGrid.actions.downloadOrderedProductSheet,
              // lineSheetableGrid.actions.copyOrderSheet,
              // lineSheetableGrid.actions.updateToBeSubmitted,
            ]),
          ],
          onClick,
          onCheck,
          onCheckAll,
        };
      case "BOUTIQUE":
        return {
          gap: "8px",
          rowType: "OrderSheet",
          // 선택(체크박스)은 바이어의 삭제·주문에만 쓰인다 — 부티크는 에이전시처럼 선택 없이 본다
          isCheckable: false,
          showCheckAll: false,
          columns: [
            orderSheetGridPairs.type.column,
            orderSheetGridPairs.name.column,
            orderSheetGridPairs.brand.column,
            orderSheetGridPairs.orderedAmount.column,
            // 에이전시와 같이 — 확정된 금액이 있으면 오더 금액 옆에 확정 금액을 보여준다
            ...(orderSheetSet && orderSheetSet.confirmedAmount > 0
              ? [orderSheetGridPairs.confirmedAmount.column]
              : []),
            orderSheetGridPairs.status.column,
          ],
          fields: [
            orderSheetGridPairs.type.field,
            orderSheetGridPairs.name.field,
            orderSheetGridPairs.brand.field,
            orderSheetGridPairs.orderedAmount.field,
            ...(orderSheetSet && orderSheetSet.confirmedAmount > 0
              ? [orderSheetGridPairs.confirmedAmount.field]
              : []),
            orderSheetGridPairs.status.field,
          ],
          indicator,
          extraContents:
            lineSheetableGrid.extraContents.orderSheetExtraContents,
          actions: [
            ...withEditOnlyGridAction([
              lineSheetableGrid.actions.deleteLineSheetable,
            ]),
            ...withReadOnlyGridActions([
              lineSheetableGrid.actions.writeDraftByOrderSheet,
              lineSheetableGrid.actions.editOrViewOrderSheet,
              lineSheetableGrid.actions.uploadFallbackOrderSheet,
              lineSheetableGrid.actions.deleteOrderSheet,
              lineSheetableGrid.actions.downloadLineSheet,
              lineSheetableGrid.actions.downloadLineSheetUnoForm,
              // 이 오더의 확정 수량 엑셀(누를 때 만든다)
              lineSheetableGrid.actions.downloadOrderSheetConfirmedOrigin,
              lineSheetableGrid.actions.downloadOrderSheetConfirmedUnoForm,
            ]),
          ],
          onClick,
          onCheck,
          onCheckAll,
        };
      default:
        return {
          gap: "8px",
          rowType: "OrderSheet",
          isCheckable: false,
          showCheckAll: false,
          columns: [
            orderSheetGridPairs.type.column,
            orderSheetGridPairs.name.column,
            orderSheetGridPairs.brand.column,
            orderSheetGridPairs.orderedAmount.column,
            orderSheetGridPairs.status.column,
          ],
          fields: [
            orderSheetGridPairs.type.field,
            orderSheetGridPairs.name.field,
            orderSheetGridPairs.brand.field,
            orderSheetGridPairs.orderedAmount.field,
            orderSheetGridPairs.status.field,
          ],
          indicator,
          extraContents:
            lineSheetableGrid.extraContents.orderSheetExtraContents,
          actions: [
            ...withEditOnlyGridAction([
              lineSheetableGrid.actions.deleteLineSheetable,
            ]),
            ...withReadOnlyGridActions([
              lineSheetableGrid.actions.writeDraftByOrderSheet,
              lineSheetableGrid.actions.editOrViewOrderSheet,
              lineSheetableGrid.actions.uploadFallbackOrderSheet,
              lineSheetableGrid.actions.deleteOrderSheet,
              lineSheetableGrid.actions.downloadLineSheet,
              lineSheetableGrid.actions.downloadLineSheetUnoForm,
              // lineSheetableGrid.actions.copyOrderSheet,
              // lineSheetableGrid.actions.updateToBeSubmitted,
            ]),
          ],
          onClick,
          onCheck,
          onCheckAll,
        };
    }
  }, [
    identity?.company?.type,
    indicator,
    isEditing,
    lineSheetableGrid.actions,
    lineSheetableGrid.extraContents.orderSheetExtraContents,
    orderSheetGridPairs.name.column,
    orderSheetGridPairs.name.field,
    orderSheetGridPairs.brand.column,
    orderSheetGridPairs.brand.field,
    orderSheetGridPairs.orderedAmount.column,
    orderSheetGridPairs.orderedAmount.field,
    orderSheetGridPairs.confirmedAmount.column,
    orderSheetGridPairs.confirmedAmount.field,
    orderSheetGridPairs.status.column,
    orderSheetGridPairs.status.field,
    orderSheetGridPairs.type.column,
    orderSheetGridPairs.type.field,
    orderSheetSet,
    setRows,
    withEditOnlyGridAction,
    withReadOnlyGridActions,
  ]);

  // 라인시트세트 자체를 볼 때(오더 하나를 보는 화면이 아닐 때) 라인시트마다 주문된 오더시트를 접어 둔다.
  // 라인시트 행에는 오더 합계만 보이고, 화살표를 누르면 오더시트가 하위 행으로 펼쳐진다.
  const showOrderSheets =
    !isEditing &&
    !orderSheetSet &&
    (identity?.company?.type === "AGENCY" ||
      identity?.company?.type === "BOUTIQUE");
  const isBoutique = identity?.company?.type === "BOUTIQUE";
  const {
    navigate,
    navigateToOrderSheetSetPage,
    navigateToOrderSheetSetDraftPage,
  } = useRouterHelper();
  const appGrid = useAppGrid();
  const {
    itemsByLineSheetId: orderSheetItemsByLineSheetId,
    isLoading: isOrderSheetItemsLoading,
  } = useLineSheetOrderSheetItems(values.id, showOrderSheets);
  const [expandedLineSheetIds, setExpandedLineSheetIds] = useState<number[]>(
    []
  );

  const toggleLineSheetExpanded = useCallback((lineSheetId: number) => {
    setExpandedLineSheetIds((prev) =>
      prev.includes(lineSheetId)
        ? prev.filter((id) => id !== lineSheetId)
        : [...prev, lineSheetId]
    );
  }, []);

  // 라인시트별로 카드 안 표에 들어갈 오더시트 줄
  const orderSheetRowsByLineSheetId = useMemo(() => {
    const map = new Map<number, LineSheetOrderSheetRow[]>();
    rows?.forEach((row) => {
      if (!isLineSheetRow(row)) {
        return;
      }
      const items = orderSheetItemsByLineSheetId.get(row.id) ?? [];
      if (items.length === 0) {
        return;
      }
      map.set(
        row.id,
        items.map(
          (item): LineSheetOrderSheetRow => ({
            ...gridRowFactory(row, "OrderSheetSet", {
              id: item.orderSheet.id,
            }),
            ...item,
            isFallbackLineSheet:
              !!row.status && isFallbackLineSheet({ status: row.status }),
            lineSheetFile: isStoredObject(row.file) ? row.file : null,
            lineSheetUnoFormFile:
              row.unoFormFile && isStoredObject(row.unoFormFile)
                ? row.unoFormFile
                : null,
          })
        )
      );
    });
    return map;
  }, [rows, orderSheetItemsByLineSheetId]);

  const gridRows = useMemo(() => {
    if (!showOrderSheets) {
      return rows;
    }
    // 폼 상태(rows)는 건드리지 않고 화면에 넘기는 행에만 펼침 상태를 얹는다 — 저장 로직과 무관하게.
    // 오더시트는 하위 행이 아니라 카드 안 표로 그리므로 하위 행은 두지 않는다.
    const rowsWithOrderSheets = rows?.map((row) =>
      isLineSheetRow(row)
        ? {
            ...row,
            isCollapsed: !expandedLineSheetIds.includes(row.id),
            children: [],
          }
        : row
    );
    // 오더가 있는 라인시트를 위로 올린다. 그 안에서는 원래 순서를 지킨다(안정 정렬).
    const hasOrderSheets = (row: LineSheetableRow) =>
      orderSheetRowsByLineSheetId.has(row.id) && isLineSheetRow(row) ? 0 : 1;
    return rowsWithOrderSheets
      ?.map((row, index) => ({ row, index }))
      .sort(
        (a, b) =>
          hasOrderSheets(a.row) - hasOrderSheets(b.row) || a.index - b.index
      )
      .map(({ row }) => row);
  }, [showOrderSheets, rows, orderSheetRowsByLineSheetId, expandedLineSheetIds]);

  const formatOrderAmount = useCallback(
    (amount: number) =>
      tCurrencyString(amount, { type: "SYMBOL", code: values.currency }),
    [tCurrencyString, values.currency]
  );

  // 표 한 줄을 누르면 상품별 주문 수량을 보는 오더시트 상세로 바로 간다.
  // 파일만 있는 라인시트는 그 화면이 없어 오더(오더시트세트) 화면으로 보낸다.
  // (부티크) 통합 주문 상품 화면이 그 오더 한 건만 보여주고, 거기서 확정 수량을 넣는다.
  const openOrderSheet = useCallback(
    (row: LineSheetOrderSheetRow) => {
      const { orderSheetSet: set, orderSheet, isFallbackLineSheet } = row;
      if (isFallbackLineSheet) {
        if (values.id) {
          navigateToOrderSheetSetPage(values.id, set.id);
        }
      } else if (isBoutique) {
        navigate(
          `/line-sheet-sets/${values.id}/ordered-products?line_sheet_id=${orderSheet.lineSheet.id}&order_sheet_set_id=${set.id}`,
          { appendReferer: true }
        );
      } else {
        navigateToOrderSheetSetDraftPage(set.id, orderSheet.id);
      }
    },
    [
      isBoutique,
      navigate,
      navigateToOrderSheetSetDraftPage,
      navigateToOrderSheetSetPage,
      values.id,
    ]
  );

  // 오더시트 세트 화면의 오더시트 줄과 같은 다운로드(발행사 양식 / 표준화 양식)
  // + (부티크) 이 오더의 확정 수량 엑셀(누를 때 만든다)
  const orderSheetDownloadActions = useCallback(
    (row: LineSheetOrderSheetRow): AppLineCardAction[] => {
      const confirmedTarget = {
        lineSheetId: row.orderSheet.lineSheet.id,
        orderSheetSetId: row.orderSheetSet.id,
        isLineSheetParsed: !row.isFallbackLineSheet,
        lineSheetFileName: row.lineSheetFile?.name,
        confirmedQuantity: row.orderSheet.confirmedQuantity,
        isCanceled: row.orderSheet.status === "CANCELED",
      };
      return [
        appGrid.actions.withDownloadUnoFile<LineSheetOrderSheetRow>(
          (r) => r.orderSheet.exportedFile || r.lineSheetFile,
          "origin_download"
        )(row),
        appGrid.actions.withDownloadUnoFile<LineSheetOrderSheetRow>(
          (r) => r.orderSheet.unoFormFile || r.lineSheetUnoFormFile,
          "uno_form_download"
        )(row),
        lineSheetableGrid.actions.orderConfirmedExport(confirmedTarget, "ORIGIN"),
        lineSheetableGrid.actions.orderConfirmedExport(
          confirmedTarget,
          "UNO_FORM"
        ),
      ].filter((action): action is AppLineCardAction => !!action);
    },
    [appGrid.actions, lineSheetableGrid.actions]
  );

  const lineSheetWithOrderSheetsGridMeta = useMemo((): AppGridMeta => {
    if (!showOrderSheets) {
      return lineSheetGridMeta;
    }
    // [구분, 제목, 브랜드] 뒤에 오더·확정 합계 칸을 끼운다(에이전시는 그 뒤에 상태 칸이 이어진다).
    const insertAt = 3;
    const twoLineHeader = (top: string, bottom: string) => (
      <Flex flexDirection={"column"} alignItems={"flex-end"}>
        <Text>{top}</Text>
        <Text>{bottom}</Text>
      </Flex>
    );
    const summaryColumns: AppGridColumn[] = [
      {
        name: "order_count",
        width: "72px",
        textAlign: "end",
        marginRight: "8px",
        value: <Text>총 주문 수</Text>,
      },
      {
        name: "ordered_confirmed_quantity",
        width: "110px",
        textAlign: "end",
        marginRight: "8px",
        value: twoLineHeader(
          tTitle("ordered_quantity"),
          tTitle("confirmed_quantity")
        ),
      },
      {
        name: "ordered_confirmed_amount",
        width: "150px",
        textAlign: "end",
        marginRight: "8px",
        value: twoLineHeader(
          tTitle("ordered_amount"),
          tTitle("confirmed_amount")
        ),
      },
    ];
    // 라인시트 줄의 합계 — 펼친 오더 표처럼 주문(위)·확정(아래)을 나눠 보여준다. 합계는 취소 오더를 뺀다.
    const summaryOf = (row: GridRow) => {
      const items = orderSheetRowsByLineSheetId.get(row.id) ?? [];
      const activeItems = items.filter(
        (item) => item.orderSheet.status !== "CANCELED"
      );
      const confirmedQuantity = sumBy(
        activeItems,
        (item) => item.orderSheet.confirmedQuantity
      );
      const confirmedAmount = sumBy(
        activeItems,
        (item) => item.orderSheet.confirmedAmount
      );
      return {
        items,
        activeItems,
        canceledCount: items.length - activeItems.length,
        isConfirmed: !!confirmedQuantity || !!confirmedAmount,
        confirmedQuantity,
        confirmedAmount,
      };
    };
    const quantityText = (quantity: number) =>
      `${quantity.toLocaleString()}${t("quantity")}`;
    // 합계 칸을 눌러도 오더 목록이 접고 펼쳐진다 — 화살표만으로는 누를 자리가 좁아
    // 라인시트 상세로 잘못 들어가는 일이 잦았다.
    const summaryField = (
      name: string,
      render: (summary: ReturnType<typeof summaryOf>) => ReactNode
    ): AppGridField => ({
      name,
      value: (row) => {
        const summary = summaryOf(row);
        if (summary.items.length === 0) {
          return (
            <OrderedConfirmedCell
              unconfirmedLabel={t("unconfirmed")}
              isLoading={isOrderSheetItemsLoading}
            />
          );
        }
        return (
          <OrderToggleArea
            isCollapsed={row.isCollapsed}
            onToggle={() => toggleLineSheetExpanded(row.id)}
            width={"100%"}
            paddingY={"4px"}
          >
            {render(summary)}
          </OrderToggleArea>
        );
      },
    });
    const summaryFields: AppGridField[] = [
      // 총 주문 수 — 취소 오더는 합계에서 빼고, 있으면 아래 줄에 따로 적는다
      summaryField("order_count", (summary) => (
        <Flex
          width={"100%"}
          flexDirection={"column"}
          alignItems={"flex-end"}
          paddingRight={"8px"}
          whiteSpace={"nowrap"}
        >
          <Text>{`${summary.activeItems.length.toLocaleString()}건`}</Text>
          {summary.canceledCount > 0 && (
            <Text fontSize={"10px"} color={"#8F8F8C"}>
              {`취소 ${summary.canceledCount}`}
            </Text>
          )}
        </Flex>
      )),
      summaryField("ordered_confirmed_quantity", (summary) => (
        <OrderedConfirmedCell
          ordered={quantityText(
            sumBy(summary.activeItems, (item) => item.orderSheet.orderedQuantity)
          )}
          confirmed={
            summary.isConfirmed
              ? quantityText(summary.confirmedQuantity)
              : undefined
          }
          unconfirmedLabel={t("unconfirmed")}
        />
      )),
      summaryField("ordered_confirmed_amount", (summary) => (
        <OrderedConfirmedCell
          ordered={formatOrderAmount(
            sumBy(summary.activeItems, (item) => item.orderSheet.orderedAmount)
          )}
          confirmed={
            summary.isConfirmed
              ? formatOrderAmount(summary.confirmedAmount)
              : undefined
          }
          unconfirmedLabel={t("unconfirmed")}
        />
      )),
    ];

    const baseIndicator = lineSheetGridMeta.indicator;
    const baseExtraContents = lineSheetGridMeta.extraContents;
    // (부티크) 확정 수량 합계 엑셀은 그 라인시트에 확정 수량이 들어간 오더가 있을 때만 받을 수 있다
    const confirmedTotalActions = [
      lineSheetableGrid.actions.downloadOrderTotalOriginConfirmed,
      lineSheetableGrid.actions.downloadOrderTotalUnoFormConfirmed,
    ];
    const hasConfirmedQuantity = (row: GridRow) =>
      (orderSheetRowsByLineSheetId.get(row.id) ?? []).some(
        (item) =>
          item.orderSheet.status !== "CANCELED" &&
          item.orderSheet.confirmedQuantity > 0
      );
    const actions = lineSheetGridMeta.actions?.map((action) =>
      confirmedTotalActions.includes(action)
        ? (row: GridRow) => {
            const result = action(row);
            if (!result || hasConfirmedQuantity(row)) {
              return result;
            }
            return {
              ...result,
              isDisabled: true,
              disabledMessage: tTitle("common:boutique_order_total.no_confirmed"),
            };
          }
        : action
    );
    return {
      ...lineSheetGridMeta,
      actions,
      columns: [
        ...lineSheetGridMeta.columns.slice(0, insertAt),
        ...summaryColumns,
        ...lineSheetGridMeta.columns.slice(insertAt),
      ],
      fields: [
        ...lineSheetGridMeta.fields.slice(0, insertAt),
        ...summaryFields,
        ...lineSheetGridMeta.fields.slice(insertAt),
      ],
      // 오더가 있는 행은 화살표로 접고 펼친다. 행 자체를 누르면 지금처럼 라인시트가 열린다.
      indicator: (row) => {
        if (!orderSheetRowsByLineSheetId.has(row.id)) {
          return baseIndicator ? (
            baseIndicator(row)
          ) : (
            <GridRowIndicator type={"EMPTY"} isHovered={row.isHovered} />
          );
        }
        // 화살표 칸은 행 높이 전체를 누를 수 있게 늘린다
        return (
          <OrderToggleArea
            isCollapsed={row.isCollapsed}
            onToggle={() => toggleLineSheetExpanded(row.id)}
            alignSelf={"stretch"}
            justifyContent={"center"}
          >
            <GridRowIndicator
              type={"COLLAPSE"}
              isHovered={true}
              isCollapsed={row.isCollapsed}
            />
          </OrderToggleArea>
        );
      },
      // 펼치면 라인시트 카드가 아래로 길어지고, 그 안에 오더시트 표가 들어간다.
      extraContents: (row, meta) => {
        const base = baseExtraContents ? baseExtraContents(row, meta) : [];
        const items = orderSheetRowsByLineSheetId.get(row.id);
        if (row.isCollapsed || !items) {
          return base;
        }
        return [
          ...base,
          <LineSheetOrderSheetTable
            key={`line_sheet_order_sheets_${row.id}`}
            rows={items}
            formatAmount={formatOrderAmount}
            getActions={orderSheetDownloadActions}
            onOpen={openOrderSheet}
            onViewAllOrderedProducts={
              isBoutique
                ? () =>
                    navigate(
                      `/line-sheet-sets/${values.id}/ordered-products?line_sheet_id=${row.id}`,
                      { appendReferer: true }
                    )
                : undefined
            }
          />,
        ];
      },
    };
  }, [
    t,
    tTitle,
    lineSheetableGrid.actions,
    showOrderSheets,
    lineSheetGridMeta,
    isOrderSheetItemsLoading,
    orderSheetRowsByLineSheetId,
    formatOrderAmount,
    toggleLineSheetExpanded,
    orderSheetDownloadActions,
    openOrderSheet,
    isBoutique,
    navigate,
    values.id,
  ]);

  return (
    <>
      <BrandSelectModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        initialValues={brandsForModal}
        saveSelectedBrand={saveSelectedBrand}
        lineSheetId={lineSheetIdForModal}
      />
      <AppGrid
        isAnchored={false}
        showNoRows={!isEditing}
        rows={gridRows}
        metas={
          orderSheetSet
            ? [orderSheetGridMeta, lineSheetGridMeta, attachmentGridMeta]
            : [
                lineSheetWithOrderSheetsGridMeta,
                orderSheetGridMeta,
                attachmentGridMeta,
              ]
        }
      />
      {/* eslint-disable-next-line react/jsx-no-undef */}
    </>
  );
}
